/**
 * Simular tributação: o MESMO motor da prévia e da emissão. Nunca emite nem consome número.
 * Pode incluir regras em rascunho; mostra regras escolhidas, descartadas e por quê; salva o
 * cenário como caso de teste (reexecutado quando uma regra ganha versão nova).
 */
import {
  FISCAL_UFS,
  formatMoney,
  type CalcResult,
  type SimulationInput,
  type TestCaseView,
  type Uf,
} from "@cafegestor/shared";
import { useState } from "react";
import { useCan } from "@/app/me";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Table,
} from "@/components/ui";
import { apiFetch, newIdempotencyKey } from "@/lib/api";
import { toDecimal } from "@/lib/decimal";
import { Checkbox, SaveError } from "../cadastros/widgets";
import { ProductPicker } from "../estoque/pickers";
import { CustomerPicker } from "../vendas/pickers";
import { CalcView } from "./CalcView";
import { useFiscalAction, useFiscalQuery, useOperations } from "./lib";

type Line = {
  productId: string;
  label: string;
  quantity: string;
  unitPrice: string;
  discount: string;
};

export function SimuladorPage() {
  const can = useCan();
  const ops = useOperations();
  const [operationId, setOperationId] = useState("");
  const [mode, setMode] = useState<"cliente" | "avulso">("cliente");
  const [customer, setCustomer] = useState({ id: "", label: "" });
  const [recipient, setRecipient] = useState({
    uf: "GO" as Uf,
    personKind: "pj" as "pf" | "pj",
    ieIndicator: "1" as "1" | "2" | "9",
    finalConsumer: false,
    ruralProducer: false,
    name: "Destinatário avulso",
  });
  const [date, setDate] = useState("");
  const [model, setModel] = useState<"" | "55" | "65">("");
  const [includeDrafts, setIncludeDrafts] = useState(true);
  const [lines, setLines] = useState<Line[]>([
    { productId: "", label: "", quantity: "1", unitPrice: "", discount: "0" },
  ]);
  const [result, setResult] = useState<CalcResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [caseName, setCaseName] = useState("");

  const input = (): SimulationInput => ({
    operationId,
    customerId: mode === "cliente" ? customer.id : null,
    recipient: mode === "avulso" ? recipient : null,
    ...(date ? { date } : {}),
    model: model || null,
    includeDrafts,
    items: lines
      .filter((l) => l.productId)
      .map((l) => ({
        productId: l.productId,
        quantity: toDecimal(l.quantity),
        unitPrice: toDecimal(l.unitPrice),
        discount: toDecimal(l.discount, "dinheiro") || "0",
      })),
  });
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(
        await apiFetch<CalcResult>("/fiscal/simulador", {
          method: "POST",
          body: input(),
          idempotencyKey: newIdempotencyKey(),
        }),
      );
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const saveCase = useFiscalAction<Record<string, unknown>>(
    () => "/casos-teste",
    "POST",
    () => setCaseName(""),
  );

  return (
    <div className="space-y-6">
      <PageTitle
        title="Simular tributação"
        description="Teste a matriz sem emitir nada: o cálculo é o mesmo da prévia e da emissão. Rascunhos só entram aqui, nunca numa nota."
      />
      <Card title="Cenário">
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Operação fiscal">
              <Select value={operationId} onChange={(e) => setOperationId(e.target.value)}>
                <option value="">Escolha...</option>
                {ops.data?.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.code} — {o.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Data (vigência)">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Modelo">
              <Select value={model} onChange={(e) => setModel(e.target.value as "" | "55" | "65")}>
                <option value="">Padrão da operação</option>
                <option value="55">NF-e (55)</option>
                <option value="65">NFC-e (65)</option>
              </Select>
            </Field>
            <div className="pt-6">
              <Checkbox
                label="Incluir regras em rascunho"
                checked={includeDrafts}
                onChange={(e) => setIncludeDrafts(e.target.checked)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={mode === "cliente" ? "primary" : "secondary"}
              onClick={() => setMode("cliente")}
            >
              Cliente cadastrado
            </Button>
            <Button
              size="sm"
              variant={mode === "avulso" ? "primary" : "secondary"}
              onClick={() => setMode("avulso")}
            >
              Perfil avulso
            </Button>
          </div>
          {mode === "cliente" ? (
            <Field label="Cliente">
              <CustomerPicker
                value={customer.id}
                label={customer.label}
                onChange={(id, label) => setCustomer({ id, label })}
              />
            </Field>
          ) : (
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <Field label="UF">
                <Select
                  value={recipient.uf}
                  onChange={(e) => setRecipient({ ...recipient, uf: e.target.value as Uf })}
                >
                  {FISCAL_UFS.map((u) => (
                    <option key={u}>{u}</option>
                  ))}
                </Select>
              </Field>
              <Field label="PF/PJ">
                <Select
                  value={recipient.personKind}
                  onChange={(e) =>
                    setRecipient({ ...recipient, personKind: e.target.value as "pf" | "pj" })
                  }
                >
                  <option value="pf">PF</option>
                  <option value="pj">PJ</option>
                </Select>
              </Field>
              <Field label="Contribuinte">
                <Select
                  value={recipient.ieIndicator}
                  onChange={(e) =>
                    setRecipient({ ...recipient, ieIndicator: e.target.value as "1" | "2" | "9" })
                  }
                >
                  <option value="1">Contribuinte</option>
                  <option value="2">Isento</option>
                  <option value="9">Não contribuinte</option>
                </Select>
              </Field>
              <div className="pt-6">
                <Checkbox
                  label="Consumidor final"
                  checked={recipient.finalConsumer}
                  onChange={(e) => setRecipient({ ...recipient, finalConsumer: e.target.checked })}
                />
              </div>
              <div className="pt-6">
                <Checkbox
                  label="Produtor rural"
                  checked={recipient.ruralProducer}
                  onChange={(e) => setRecipient({ ...recipient, ruralProducer: e.target.checked })}
                />
              </div>
            </div>
          )}
          <Table head={["Produto", "Quantidade (unid. base)", "Preço unitário", "Desconto", ""]}>
            {lines.map((l, n) => (
              <tr key={n}>
                <td className="px-3 py-2">
                  <ProductPicker
                    value={l.productId}
                    label={l.label}
                    onChange={(id, label) =>
                      setLines(lines.map((x, i) => (i === n ? { ...x, productId: id, label } : x)))
                    }
                  />
                </td>
                <td className="text-right tabular-nums px-3 py-2">
                  <Input
                    value={l.quantity}
                    onChange={(e) =>
                      setLines(
                        lines.map((x, i) => (i === n ? { ...x, quantity: e.target.value } : x)),
                      )
                    }
                  />
                </td>
                <td className="text-right tabular-nums px-3 py-2">
                  <Input
                    value={l.unitPrice}
                    onChange={(e) =>
                      setLines(
                        lines.map((x, i) => (i === n ? { ...x, unitPrice: e.target.value } : x)),
                      )
                    }
                  />
                </td>
                <td className="text-right tabular-nums px-3 py-2">
                  <Input
                    value={l.discount}
                    onChange={(e) =>
                      setLines(
                        lines.map((x, i) => (i === n ? { ...x, discount: e.target.value } : x)),
                      )
                    }
                  />
                </td>
                <td className="px-3 py-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setLines(lines.filter((_, i) => i !== n))}
                  >
                    Remover
                  </Button>
                </td>
              </tr>
            ))}
          </Table>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                setLines([
                  ...lines,
                  { productId: "", label: "", quantity: "1", unitPrice: "", discount: "0" },
                ])
              }
            >
              Adicionar produto
            </Button>
            <Button disabled={busy || !operationId} onClick={run}>
              {busy ? "Calculando..." : "Simular"}
            </Button>
          </div>
          <SaveError error={error} />
        </div>
      </Card>
      {result && (
        <Card
          title={
            result.ok
              ? "Resultado: pronto para emitir com estas regras"
              : "Resultado: a emissão seria bloqueada"
          }
        >
          <CalcView calc={result} />
          {can("fiscal.matriz.preparar") && (
            <div className="mt-4 flex flex-wrap items-end gap-3 rounded-md border p-3">
              <Field label="Salvar como caso de teste (com os valores atuais como esperados)">
                <Input
                  value={caseName}
                  onChange={(e) => setCaseName(e.target.value)}
                  placeholder="Ex.: Venda interna PJ contribuinte"
                />
              </Field>
              <Button
                disabled={caseName.trim().length < 2 || saveCase.isPending}
                onClick={() =>
                  saveCase.mutate({
                    name: caseName.trim(),
                    input: input(),
                    expected: {
                      totals: { document: result.totals.document, ...result.totals.taxes },
                      items: result.items.map((i) => ({
                        line: i.line,
                        ...(i.cfop ? { cfop: i.cfop } : {}),
                        taxes: Object.fromEntries(i.taxes.map((t) => [t.tax, t.amount])),
                      })),
                    },
                  })
                }
              >
                Salvar caso
              </Button>
              <SaveError error={saveCase.error} />
            </div>
          )}
        </Card>
      )}
      <TestCases />
    </div>
  );
}

function TestCases() {
  const can = useCan();
  const cases = useFiscalQuery<TestCaseView[]>("/casos-teste");
  const run = useFiscalAction<Record<string, never>, { passed: boolean }>(
    () => "/casos-teste/executar",
  );
  const archive = useFiscalAction<{ id: string; version: number }>(
    (b) => `/casos-teste/${b.id}/arquivar`,
  );
  const active = (cases.data ?? []).filter((c) => c.status === "active");
  return (
    <Card
      title="Casos de teste"
      actions={
        can("fiscal.matriz.preparar") &&
        active.length > 0 && (
          <Button size="sm" onClick={() => run.mutate({})} disabled={run.isPending}>
            Reexecutar todos (regras aprovadas)
          </Button>
        )
      }
    >
      <p className="mb-2 text-xs text-muted-foreground">
        Ao testar uma versão de regra, todos os casos ativos rodam com ela; a versão só fica
        "testada" se todos passarem e ao menos um usar a regra.
      </p>
      {run.data && (
        <Alert tone={run.data.passed ? "ok" : "warn"}>
          {run.data.passed ? "Todos os casos passaram." : "Há casos falhando — veja abaixo."}
        </Alert>
      )}
      <Table head={["Caso", "Total esperado", "Última execução", ""]}>
        {active.map((c) => (
          <tr key={c.id}>
            <td className="px-3 py-2">{c.name}</td>
            <td className="text-right tabular-nums px-3 py-2">
              {c.expected.totals?.["document"] ? formatMoney(c.expected.totals["document"]) : "—"}
            </td>
            <td className="px-3 py-2">
              {c.lastResult ? (
                <Badge tone={c.lastResult === "passou" ? "ok" : "error"}>{c.lastResult}</Badge>
              ) : (
                "—"
              )}
              {c.lastDetails && c.lastDetails.differences.length > 0 && (
                <span className="block text-xs text-muted-foreground">
                  {c.lastDetails.differences.join(" ")}
                </span>
              )}
            </td>
            <td className="px-3 py-2 text-right">
              {can("fiscal.matriz.preparar") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => archive.mutate({ id: c.id, version: c.version })}
                >
                  Arquivar
                </Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      <SaveError error={run.error ?? archive.error} />
    </Card>
  );
}
